/**
 * apiAuth.js — attaches the signed-in user's Supabase access token to every
 * same-origin /api/* request, so the server's auth gate (server/apiAuth.js)
 * lets it through. Installed once in main.jsx before the app renders.
 *
 * - fetch("/api/…")           → Authorization: Bearer <token> header
 * - new EventSource("/api/…") → ?access_token=<token> (SSE can't set headers)
 * - redirects / download links → wrap the URL with authUrl() at click time
 *
 * Tokens are never attached to other origins.
 */
import { supabase } from "./supabase.js";

let _token = null;

function isOwnApiUrl(u) {
  if (!u) return false;
  try {
    const parsed = new URL(u, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith("/api/");
  } catch { return false; }
}

// Synchronous: for URLs that a browser navigates to directly (OAuth start,
// CSV download, EventSource). Call at click time so the token is fresh.
export function authUrl(path) {
  if (!_token) return path;
  return path + (path.includes("?") ? "&" : "?") + "access_token=" + encodeURIComponent(_token);
}

export function installApiAuth() {
  if (!supabase || typeof window === "undefined" || window.__kurbApiAuthInstalled) return;
  window.__kurbApiAuthInstalled = true;

  supabase.auth.getSession().then(({ data }) => { _token = data?.session?.access_token || null; });
  supabase.auth.onAuthStateChange((_, session) => { _token = session?.access_token || null; });

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === "string" || input instanceof URL ? String(input) : input?.url;
    if (!isOwnApiUrl(url)) return nativeFetch(input, init);
    // getSession() refreshes an expired token before returning it.
    const { data } = await supabase.auth.getSession().catch(() => ({ data: null }));
    const token = data?.session?.access_token || _token;
    if (!token) return nativeFetch(input, init);
    _token = token;
    if (typeof input !== "string" && !(input instanceof URL)) {
      // Request object — clone with the header added.
      const req = new Request(input, init);
      if (!req.headers.has("Authorization")) req.headers.set("Authorization", `Bearer ${token}`);
      return nativeFetch(req);
    }
    const headers = new Headers(init?.headers || {});
    if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return nativeFetch(input, { ...init, headers });
  };

  const NativeEventSource = window.EventSource;
  if (NativeEventSource) {
    window.EventSource = class extends NativeEventSource {
      constructor(url, config) {
        super(isOwnApiUrl(String(url)) ? authUrl(String(url)) : url, config);
      }
    };
  }
}
